import { useTranslation } from 'react-i18next';

import { useUser } from '~/api/user';
import { Button, Card, ExternalLinkButton, Illustration, Separator, Text, Title, Tooltip } from '~/components';
import { env } from '~/constants/env';
import { useCopyToClipboard } from '~/hooks/useCopyToClipboard';
import { useDialog } from '~/hooks/useDialog';
import { useMediaQuery } from '~/hooks/useMediaQuery';
import { breakpoints } from '~/styles/media.css';
import { srOnlyStyle } from '~/styles/utilities.css';
import { toast } from '~/utils/toast';

import {
  buttonsRowStyle,
  buttonStyle,
  cardStyle,
  contentStyle,
  decorativePlane1Style,
  decorativePlane2Style,
  illustrationStyle,
  separatorStyle,
  socialsRowStyle,
} from './InvitationCTA.css';
import { SendInvitationDialog } from './SendInvitationDialog';

export const InvitationCTA = () => {
  const { data: user } = useUser();

  const sendInvitationDialog = useDialog();
  const [isInvitationsLinkCopied, copyToClipboard] = useCopyToClipboard();

  const isMediumViewport = useMediaQuery(breakpoints.medium);
  const { t } = useTranslation(['invitations']);

  const supportsWebShareApi = 'share' in navigator;

  const invitationLink = env.IS_PRODUCTION_ENV
    ? `https://register.creativeshelter.be/invite/${user.referralCode}`
    : `${new URL(env.API_URL).origin}/invite/${user.referralCode}`;

  const socialLinks = [
    {
      icon: 'Whatsapp',
      label: t('invitations:hero.socials.whatsapp'),
      url: `https://wa.me/?text=${encodeURIComponent(`${t('invitations:cta.share.data.text')} ${invitationLink}`)}`,
    },
    {
      icon: 'Facebook',
      label: t('invitations:hero.socials.facebook'),
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(invitationLink)}`,
    },
    {
      icon: 'LinkedIn',
      label: t('invitations:hero.socials.linkedin'),
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(invitationLink)}`,
    },
    {
      icon: 'X',
      label: t('invitations:hero.socials.x'),
      url: `https://twitter.com/share?text=${encodeURIComponent(t('invitations:cta.socials.twitter.text'))}&url=${encodeURIComponent(invitationLink)}&hashtags=${t(
        'invitations:cta.socials.twitter.hashtags',
      )
        .split(' ')
        .map((hashtag) => hashtag.slice(1))
        .join(',')}`,
    },
  ] as const;

  const shareInvitationLink = async () => {
    try {
      await navigator.share({
        url: invitationLink,
        text: t('invitations:cta.share.data.text'),
        title: t('invitations:cta.share.data.title'),
      });
    } catch (error) {
      if (error instanceof DOMException && error.name === 'NotAllowedError') {
        toast.danger({ description: t('invitations:cta.share.permissionDenied') });
      }
    }
  };

  const useNativeShare = isMediumViewport && supportsWebShareApi;

  return (
    <>
      <Card.Root className={cardStyle}>
        <Illustration className={illustrationStyle} name="Target" strokeWidth={1} />

        <Illustration className={decorativePlane1Style} name="Plane2" strokeWidth={2} />

        <Illustration className={decorativePlane2Style} name="Plane" strokeWidth={2} />

        <div className={contentStyle}>
          <Title as="h2">{t('invitations:hero.title')}</Title>

          <Text>{t('invitations:hero.description')}</Text>

          <div className={buttonsRowStyle}>
            <Button className={buttonStyle} icon="Mail" onClick={() => sendInvitationDialog.open()}>
              {t('invitations:hero.buttons.email')}
            </Button>

            {useNativeShare ? (
              <Button className={buttonStyle} emphasis="secondary" icon="Share" onClick={shareInvitationLink}>
                {t('invitations:hero.buttons.share')}
              </Button>
            ) : (
              <Tooltip content={t('invitations:hero.buttons.copyLinkSuccess')} open={isInvitationsLinkCopied}>
                <Button className={buttonStyle} emphasis="secondary" icon="Link" onClick={() => copyToClipboard(invitationLink)}>
                  {t('invitations:hero.buttons.copyLink')}
                </Button>
              </Tooltip>
            )}

            <span className={srOnlyStyle} role="status">
              {isInvitationsLinkCopied ? t('invitations:hero.buttons.copyLinkSuccess') : ''}
            </span>
          </div>

          {!useNativeShare && (
            <>
              <Separator className={separatorStyle} text={t('invitations:hero.shareVia')} />

              <div className={socialsRowStyle}>
                {socialLinks.map((socialLink) => (
                  <ExternalLinkButton aria-label={socialLink.label} href={socialLink.url} icon={socialLink.icon} key={socialLink.icon} />
                ))}
              </div>
            </>
          )}
        </div>
      </Card.Root>

      {sendInvitationDialog.isOpen && <SendInvitationDialog onClose={sendInvitationDialog.close} />}
    </>
  );
};
